import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getFirestore, collection, query, getDocs, where, orderBy } from 'firebase/firestore';
import { EyeIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';

const AdminPendingFulfillmentPage = () => {
  const [printJobs, setPrintJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userDetails, setUserDetails] = useState({});

  useEffect(() => {
    const fetchPendingJobs = async () => {
      try {
        setLoading(true);
        const db = getFirestore();
        
        // Only fetch jobs that are paid but not yet fulfilled
        const q = query(
          collection(db, 'printJobs'),
          where('paymentStatus', '==', 'PAID_AWAITING_FULFILLMENT'),
          orderBy('createdAt', 'desc')
        );
        
        const querySnapshot = await getDocs(q);
        const jobs = querySnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(),
          createdAtFormatted: doc.data().createdAt?.toDate().toLocaleDateString() || 'Unknown'
        }));
        
        setPrintJobs(jobs);
        
        // Fetch user details for each job
        const userIds = [...new Set(jobs.map(job => job.userId).filter(Boolean))];
        const userPromises = userIds.map(async userId => {
          try {
            const userDoc = await getFirestore().collection('users').doc(userId).get();
            if (userDoc.exists) {
              return { userId, ...userDoc.data() };
            }
            return { userId, email: 'Unknown' };
          } catch (err) {
            console.error(`Error fetching user ${userId}:`, err);
            return { userId, email: 'Error' };
          }
        });
        
        const users = await Promise.all(userPromises);
        const userMap = {};
        users.forEach(user => {
          userMap[user.userId] = user;
        });
        
        setUserDetails(userMap);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching pending fulfillment jobs:', error);
        setLoading(false);
      }
    };
    
    fetchPendingJobs();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Pending Fulfillment</h1>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-yellow-100 text-yellow-800 text-sm">
          {printJobs.length} {printJobs.length === 1 ? 'Order' : 'Orders'} Waiting
        </span>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      ) : printJobs.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
          No orders pending fulfillment. All caught up!
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {printJobs.map((job) => {
              const user = userDetails[job.userId] || { email: 'Unknown' };
              const formattedAmount = job.totalAmount 
                ? `$${parseFloat(job.totalAmount / 100).toFixed(2)}` 
                : job.amount 
                  ? `$${parseFloat(job.amount / 100).toFixed(2)}`
                  : 'N/A';
                  
              const luluBasePrice = job.luluBasePrice
                ? `$${parseFloat(job.luluBasePrice / 100).toFixed(2)}`
                : 'Not available';
                
              const markupAmount = (job.totalAmount && job.luluBasePrice) 
                ? `$${parseFloat((job.totalAmount - job.luluBasePrice) / 100).toFixed(2)}`
                : 'Not available';
              
              return (
                <li key={job.id}>
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-indigo-600 truncate">
                          Print Job #{job.printJobId || job.id}
                        </p>
                        <div className="ml-2 flex-shrink-0 flex">
                          <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            Paid - Needs Fulfillment
                          </p>
                        </div>
                      </div>
                      <div className="ml-2 flex-shrink-0 flex space-x-2">
                        <Link
                          to={`/admin/print-jobs/${job.id}`}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <EyeIcon className="mr-1.5 h-4 w-4" />
                          View
                        </Link>
                        <Link
                          to={`/admin/print-jobs/${job.id}?action=fulfill`}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <ClipboardDocumentCheckIcon className="mr-1.5 h-4 w-4" />
                          Process
                        </Link>
                      </div>
                    </div>
                    <div className="mt-2 sm:flex sm:justify-between">
                      <div className="sm:flex">
                        <p className="flex items-center text-sm text-gray-500">
                          Customer: {user.displayName || user.email}
                        </p>
                        <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                          Book ID: {job.bookId || 'N/A'}
                        </p>
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                        <div className="flex flex-col">
                          <p><span className="font-medium">Total Price:</span> {formattedAmount}</p>
                          <p><span className="font-medium">Lulu Cost:</span> {luluBasePrice}</p>
                          <p><span className="font-medium">Markup:</span> {markupAmount}</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Created: {job.createdAtFormatted}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AdminPendingFulfillmentPage;
